/**
 * MainPage — Phase-10/11-Dashboard fuer Auftragsverfolgung.
 *
 * Architektur:
 *   Header (Programm-Branding + Refresh-Button)
 *   Tabs Paket DHL / Lieferanten-Versand (Phase 11)
 *   Banner-Text pro Tab (Phase 11)
 *   DashboardChart (Donut + Mini-Balken, click-handler scrollen zu Sektionen)
 *   FilterBar (controlled, showCarrierFilter=false auf Lieferanten-Tab)
 *   SectionList (Paket: 5 Sektionen; Lieferanten: 4 Sektionen)
 *   OrderDetailDrawer (on demand)
 *
 * State:
 *   - stats: DashboardStatsResponse | null  — fuer den Chart + die Sektion-Counts
 *   - sectionsState: Partial<Record<SectionId, SectionState>>  — pro-Sektion Orders/Pagination/Loading
 *   - subFilter: { dhlStatus }  — Sektion-Carrier client-side Sub-Filter, NICHT persistent
 *   - collapsed: CollapsedState  — Sektion-Akkordeon, persistiert in localStorage
 *
 * Lazy-Load + Race-Condition-Schutz:
 *   - cancelledRef ist Map<string, {cancelled:boolean}> — keys: 'stats' + 'section:<id>'
 *   - Mount + Tab-Wechsel + reloadKey-Increment: cancel old, reset states, refetch
 *   - Sektion-Expand erstes mal: lazy-load
 *   - useEffect-Deps: [kategorie, filters, reloadKey] — collapsed absichtlich NICHT dabei,
 *     damit toggleCollapsed keinen Mass-Refetch ausloest
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { PackageSearch, RefreshCw, AlertCircle, Settings } from 'lucide-react';
import { api } from '@libero/core/services/api';
import FilterBar, { type FilterState } from '../components/FilterBar';
import SectionTabs, { type SectionTabItem } from '../components/SectionTabs';
import OrderDetailDrawer from '../components/OrderDetailDrawer';
import DashboardChart from '../components/DashboardChart';
import SectionList, {
  type SectionListHandle,
  type SectionState,
  type SubFilterState,
} from '../components/SectionList';
import SettingsTab from '../components/SettingsTab';
import StatisticsTab from '../components/StatisticsTab';
import SyncStatusBar from '../components/SyncStatusBar';
import { type OrderListItem } from '../lib/groupOrders';
import {
  buildSectionQuery,
  type SectionId,
  type VersandKategorie,
} from '../lib/sectionQueryBuilder';
import {
  readCollapsed,
  writeCollapsed,
  type CollapsedState,
} from '../lib/sectionStorage';
import type {
  DashboardStatsResponse,
  InternalStatusKey,
} from '../lib/dashboardStatsMapper';

interface ListResponse {
  orders: OrderListItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const INITIAL_FILTERS: FilterState = {
  status: [],
  hasPackage: 'all',
  carrier: 'all',
  onlyStuck: false,
  search: '',
  sortBy: 'stuck_first',  // Phase 16: Default-Sortierung Stuck oben, dann aelteste
};

/** Phase 17: INITIAL_FILTERS mit localStorage-Werten ueberlagern (Sortierung + onlyStuck persistent). */
function loadInitialFilters(): FilterState {
  const fromLs: Partial<FilterState> = {};
  try {
    const sortVal = globalThis.localStorage?.getItem(LS_KEY_SORT_BY);
    if (sortVal === 'stuck_first' || sortVal === 'oldest_first' || sortVal === 'newest_first') {
      fromLs.sortBy = sortVal;
    }
    const stuckVal = globalThis.localStorage?.getItem(LS_KEY_ONLY_STUCK);
    if (stuckVal === 'true' || stuckVal === 'false') {
      fromLs.onlyStuck = stuckVal === 'true';
    }
  } catch { /* localStorage unavailable */ }
  return { ...INITIAL_FILTERS, ...fromLs };
}

// Phase 11+14+16: Tab-spezifische Section-IDs
const PAKET_SECTION_IDS: SectionId[] = ['stuck', 'carrier', 'neverShipped', 'waiting', 'manualOnly', 'done'];
const LIEFERANTEN_SECTION_IDS: SectionId[] = [
  'huck', 'huckDone',
  'helo', 'heloDone',
  'glawel', 'glawelDone',
  'sporttore', 'sporttoreDone',
  'wff', 'wffDone',
];
const FOLLOWUP_SECTION_IDS: SectionId[] = ['followup'];

// Phase 17: Tab-Layout — pro Tab welche SectionIds als Reiter dargestellt werden.
// Hidden im Reiter-Menue: manualOnly (kommt nur bei N>0 als Reiter ans Ende).
const PAKET_TAB_IDS: SectionId[] = ['stuck', 'carrier', 'neverShipped', 'waiting', 'manualOnly', 'done'];
// Lieferanten-Tab: 5 Lieferant-Reiter, pro Lieferant zweiter Reiter "Offen | Abgeschlossen"
const LIEFERANTEN_SUPPLIERS = ['huck', 'helo', 'glawel', 'sporttore', 'wff'] as const;
type SupplierKey = (typeof LIEFERANTEN_SUPPLIERS)[number];
const SUPPLIER_LABELS_DE: Record<SupplierKey, string> = {
  huck: 'Huck',
  helo: 'Helo',
  glawel: 'GlaWel',
  sporttore: 'Sporttore',
  wff: 'WFF',
};

const LS_KEY_PAKET_TAB = 'auftragsverfolgung.activeTab.paket';
const LS_KEY_LIEF_SUPPLIER = 'auftragsverfolgung.activeTab.lieferantenSupplier';
const LS_KEY_LIEF_SUB = 'auftragsverfolgung.activeTab.lieferantenSub';
const LS_KEY_SORT_BY = 'auftragsverfolgung.sortBy';
const LS_KEY_ONLY_STUCK = 'auftragsverfolgung.onlyStuck';

function readLS(key: string, fallback: string): string {
  try { return globalThis.localStorage?.getItem(key) ?? fallback; } catch { return fallback; }
}
function writeLS(key: string, value: string): void {
  try { globalThis.localStorage?.setItem(key, value); } catch { /* no-op */ }
}

function getSectionIds(kategorie: VersandKategorie): SectionId[] {
  if (kategorie === 'lieferanten') return LIEFERANTEN_SECTION_IDS;
  if (kategorie === 'followup') return FOLLOWUP_SECTION_IDS;
  return PAKET_SECTION_IDS;
}

function emptySectionState(): SectionState {
  return {
    orders: [],
    page: 1,
    totalPages: 0,
    total: 0,
    loading: false,
    error: null,
    initialized: false,
  };
}

function buildEmptySectionsState(kategorie: VersandKategorie): Partial<Record<SectionId, SectionState>> {
  const ids = getSectionIds(kategorie);
  return ids.reduce((acc, id) => {
    acc[id] = emptySectionState();
    return acc;
  }, {} as Partial<Record<SectionId, SectionState>>);
}

export default function MainPage() {
  const [filters, setFilters] = useState<FilterState>(() => loadInitialFilters());
  const [kategorie, setKategorie] = useState<VersandKategorie>('paket');
  const [activeView, setActiveView] = useState<'dashboard' | 'statistik' | 'einstellungen'>('dashboard');
  const [collapsed, setCollapsed] = useState<CollapsedState>(() => readCollapsed());
  const [sectionsState, setSectionsState] = useState<Partial<Record<SectionId, SectionState>>>(
    () => buildEmptySectionsState('paket'),
  );
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [subFilter, setSubFilter] = useState<SubFilterState>({ dhlStatus: null });
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Phase 17: Aktive Tab-Auswahl pro Tab-Kategorie + localStorage-Persistenz
  const [activePaketTab, setActivePaketTab] = useState<SectionId>(
    () => (readLS(LS_KEY_PAKET_TAB, 'stuck') as SectionId) ?? 'stuck',
  );
  const [activeLieferantenSupplier, setActiveLieferantenSupplier] = useState<SupplierKey>(
    () => (readLS(LS_KEY_LIEF_SUPPLIER, 'huck') as SupplierKey) ?? 'huck',
  );
  const [activeLieferantenSub, setActiveLieferantenSub] = useState<'offen' | 'abgeschlossen'>(
    () => (readLS(LS_KEY_LIEF_SUB, 'offen') as 'offen' | 'abgeschlossen') ?? 'offen',
  );

  const sectionListRef = useRef<SectionListHandle>(null);
  // Cancel-Flags pro Sektion + stats — keys: 'stats' | 'section:<sectionId>'
  const cancelledRef = useRef<Record<string, { cancelled: boolean }>>({});

  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  // 20.08.2026: "Geklärt"-Haken im Rücksendungs-Reiter — Default: geklaerte
  // Faelle ausgeblendet. Ref-Spiegel, damit fetchSection (useCallback ohne Deps)
  // immer den aktuellen Wert liest.
  const [mitGeklaerten, setMitGeklaerten] = useState(false);
  const mitGeklaertenRef = useRef(false);

  // 24.08.2026: "Abgeschlossene anzeigen" im Nie-versendet-Reiter — Default:
  // abgeschlossene Faelle ausgeblendet (Arbeitsliste). Ref-Spiegel wie oben.
  const [mitAbgeschlossenen, setMitAbgeschlossenen] = useState(false);
  const mitAbgeschlossenenRef = useRef(false);

  // ── Stats-Fetch ──────────────────────────────────────────────────────────────
  const fetchStats = useCallback(
    (currentKategorie: VersandKategorie) => {
      const key = 'stats';
      if (cancelledRef.current[key]) cancelledRef.current[key].cancelled = true;
      const local = { cancelled: false };
      cancelledRef.current[key] = local;

      setStatsLoading(true);
      setStatsError(null);

      api
        .get(`/programmes/auftragsverfolgung/dashboard-stats?versandKategorie=${currentKategorie}`)
        .then((res: { data?: { data?: unknown } }) => {
          if (local.cancelled) return;
          setStats(res.data?.data as DashboardStatsResponse);
        })
        .catch((err: { response?: { data?: { error?: { message?: string } } }; message?: string }) => {
          if (local.cancelled) return;
          setStatsError(
            err.response?.data?.error?.message ?? err.message ?? 'Stats-Fehler',
          );
          setStats(null);
        })
        .finally(() => {
          if (!local.cancelled) setStatsLoading(false);
        });
    },
    [],
  );

  // ── Sektion-Fetch ────────────────────────────────────────────────────────────
  const fetchSection = useCallback(
    (
      sectionId: SectionId,
      page: number,
      currentFilters: FilterState,
      currentKategorie: VersandKategorie,
    ) => {
      const key = `section:${sectionId}`;
      if (cancelledRef.current[key]) cancelledRef.current[key].cancelled = true;
      const local = { cancelled: false };
      cancelledRef.current[key] = local;

      setSectionsState((prev) => ({
        ...prev,
        [sectionId]: { ...(prev[sectionId] ?? emptySectionState()), loading: true, error: null },
      }));

      const qs = buildSectionQuery({
        sectionId,
        page,
        filters: currentFilters,
        kategorie: currentKategorie,
        // 20.08.2026: nur der Rücksendungs-Reiter wertet das aus
        mitGeklaerten: mitGeklaertenRef.current,
        // 24.08.2026: nur der Nie-versendet-Reiter wertet das aus
        mitAbgeschlossenen: mitAbgeschlossenenRef.current,
      });

      api
        .get(`/programmes/auftragsverfolgung/list?${qs}`)
        .then((res: { data?: { data?: unknown } }) => {
          if (local.cancelled) return;
          const data = res.data?.data as ListResponse;
          setSectionsState((prev) => ({
            ...prev,
            [sectionId]: {
              ...(prev[sectionId] ?? emptySectionState()),
              orders: data.orders ?? [],
              total: data.total ?? 0,
              totalPages: data.totalPages ?? 0,
              page: data.page ?? page,
              loading: false,
              error: null,
              initialized: true,
            },
          }));
        })
        .catch((err: { response?: { data?: { error?: { message?: string } } }; message?: string }) => {
          if (local.cancelled) return;
          setSectionsState((prev) => ({
            ...prev,
            [sectionId]: {
              ...(prev[sectionId] ?? emptySectionState()),
              loading: false,
              error:
                err.response?.data?.error?.message ?? err.message ?? 'Ladefehler',
              initialized: true,
            },
          }));
        });
    },
    [],
  );

  // ── Mount + Filter-/Kategorie-/ReloadKey-Wechsel: Reset + fetchInitial ───────
  useEffect(() => {
    // Alle laufenden Fetches canceln
    for (const c of Object.values(cancelledRef.current)) c.cancelled = true;
    cancelledRef.current = {};

    // Section-State + Sub-Filter zuruecksetzen (Tab-spezifisch)
    setSectionsState(buildEmptySectionsState(kategorie));
    setSubFilter({ dhlStatus: null });

    // Stats refetchen
    fetchStats(kategorie);

    // Phase 17: Im Tab-Layout nur die aktive Sektion laden (kein eager-load aller Sektionen).
    // Followup-Tab (kategorie='followup') hat kein Tab-Layout → wie bisher alle expanded.
    if (kategorie === 'paket') {
      fetchSection(activePaketTab, 1, filters, kategorie);
    } else if (kategorie === 'lieferanten') {
      const sid: SectionId = activeLieferantenSub === 'abgeschlossen'
        ? (activeLieferantenSupplier === 'wff' ? 'wff' : (`${activeLieferantenSupplier}Done` as SectionId))
        : (activeLieferantenSupplier as SectionId);
      fetchSection(sid, 1, filters, kategorie);
    } else {
      // Followup-Tab: alle expanded Sektionen laden (wie vorher)
      const currentCollapsed = readCollapsed();
      for (const sid of getSectionIds(kategorie)) {
        if (!currentCollapsed[sid]) {
          fetchSection(sid, 1, filters, kategorie);
        }
      }
    }
    // collapsed absichtlich NICHT als Dep — toggleCollapsed loest lazy-load selbst aus
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategorie, filters, reloadKey]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  // Phase 17: Lieferanten-Sub-Tab → SectionId mapping
  // 'offen': huck, helo, glawel, sporttore, wff
  // 'abgeschlossen': huckDone, heloDone, glawelDone, sporttoreDone (kein wffDone — WFF unterscheidet das nicht)
  const computeLieferantenSectionId = useCallback((): SectionId => {
    if (activeLieferantenSub === 'abgeschlossen') {
      if (activeLieferantenSupplier === 'huck') return 'huckDone';
      if (activeLieferantenSupplier === 'helo') return 'heloDone';
      if (activeLieferantenSupplier === 'glawel') return 'glawelDone';
      if (activeLieferantenSupplier === 'sporttore') return 'sporttoreDone';
      // 2026-07-27: WFF hat jetzt ebenfalls eine Abgeschlossen-Sektion (manufacturer-basiert)
      return 'wffDone';
    }
    return activeLieferantenSupplier as SectionId;
  }, [activeLieferantenSupplier, activeLieferantenSub]);

  // Active Section-ID basierend auf Kategorie + Tab-State
  const activeSectionId: SectionId | null =
    kategorie === 'paket' ? activePaketTab
    : kategorie === 'lieferanten' ? computeLieferantenSectionId()
    : null;  // Followup-Tab: kein Tab-Layout

  const handleSelectPaketTab = useCallback((id: string) => {
    const sid = id as SectionId;
    setActivePaketTab(sid);
    writeLS(LS_KEY_PAKET_TAB, sid);
    // Lazy-fetch wenn noch nicht initialized
    setSectionsState((prev) => {
      if (!prev[sid]?.initialized) {
        setTimeout(() => fetchSection(sid, 1, filters, kategorie), 0);
      }
      return prev;
    });
  }, [fetchSection, filters, kategorie]);

  const handleSelectLieferantenSupplier = useCallback((id: string) => {
    const supplier = id as SupplierKey;
    setActiveLieferantenSupplier(supplier);
    writeLS(LS_KEY_LIEF_SUPPLIER, supplier);
    // Bestimmt SectionId basierend auf aktuellem Sub-Tab
    // 2026-07-27: WFF folgt jetzt demselben Muster (wff → wffDone)
    const sid: SectionId = activeLieferantenSub === 'abgeschlossen'
      ? (`${supplier}Done` as SectionId)
      : (supplier as SectionId);
    setSectionsState((prev) => {
      if (!prev[sid]?.initialized) {
        setTimeout(() => fetchSection(sid, 1, filters, kategorie), 0);
      }
      return prev;
    });
  }, [activeLieferantenSub, fetchSection, filters, kategorie]);

  const handleSelectLieferantenSub = useCallback((id: string) => {
    const sub = id as 'offen' | 'abgeschlossen';
    setActiveLieferantenSub(sub);
    writeLS(LS_KEY_LIEF_SUB, sub);
    const sid: SectionId = sub === 'abgeschlossen'
      ? (`${activeLieferantenSupplier}Done` as SectionId)
      : (activeLieferantenSupplier as SectionId);
    setSectionsState((prev) => {
      if (!prev[sid]?.initialized) {
        setTimeout(() => fetchSection(sid, 1, filters, kategorie), 0);
      }
      return prev;
    });
  }, [activeLieferantenSupplier, fetchSection, filters, kategorie]);

  // Phase 17: Build Tab-Items aus stats
  const paketTabItems: SectionTabItem[] = stats ? [
    { id: 'stuck', label: 'Stuck', count: stats.trackingStatus.stuck },
    { id: 'carrier', label: 'Beim Carrier', count: stats.trackingStatus.carrier },
    { id: 'neverShipped', label: 'Nie versendet', count: stats.trackingStatus.neverShipped },
    // 29.07.2026: DHL bucht eine Rücksendung als normale Zustellung an unserem
    // Lager — dieser Reiter macht die Fälle sichtbar. Enthält bewusst auch
    // ABGESCHLOSSENE Aufträge: genau die sind das Problem (Auftrag erledigt,
    // Ware liegt bei uns, niemand prüft Gutschrift).
    { id: 'ruecksendung', label: 'Paket zurück zu anstoss', count: stats.trackingStatus.ruecksendung },
    { id: 'waiting', label: 'Wartet auf Paket', count: stats.trackingStatus.open + stats.trackingStatus.awaiting_package },
    { id: 'manualOnly', label: 'Nur manuell', count: stats.trackingStatus.manual_only },
    { id: 'done', label: 'Abgeschlossen', count: stats.trackingStatus.done + stats.trackingStatus.closed_manually },
  ] : [];

  const lieferantenSupplierTabItems: SectionTabItem[] = stats ? LIEFERANTEN_SUPPLIERS.map((s) => ({
    id: s,
    // 2026-07-27: WFF hat jetzt ebenfalls einen Offen-Zaehler (supplierDistribution.wff)
    count: (stats.supplierDistribution as any)[s] ?? null,
    label: SUPPLIER_LABELS_DE[s],
    stuckCount: stats.supplierStuck ? stats.supplierStuck[s] : null,
  })) : [];

  // Sub-Tab "Offen | Abgeschlossen" pro Lieferant — 2026-07-27: WFF bekommt beide Reiter
  const lieferantenSubItems: SectionTabItem[] = stats ? [
    {
      id: 'offen',
      label: 'Offen',
      count: (stats.supplierDistribution as any)[activeLieferantenSupplier] ?? null,
    },
    {
      id: 'abgeschlossen',
      label: 'Abgeschlossen',
      count: stats.supplierCompleted ? (stats.supplierCompleted as any)[activeLieferantenSupplier] ?? 0 : 0,
    },
  ] : [];

  const handleFilterChange = useCallback((next: FilterState) => {
    setFilters(next);
    // Phase 17: persistiere Sortier-Option und onlyStuck dauerhaft
    writeLS(LS_KEY_SORT_BY, next.sortBy);
    writeLS(LS_KEY_ONLY_STUCK, String(next.onlyStuck));
  }, []);

  const handleKategorieChange = useCallback((next: VersandKategorie) => {
    setKategorie(next);
  }, []);

  const handleToggleCollapsed = useCallback(
    (sectionId: SectionId) => {
      const isCurrentlyCollapsed = collapsed[sectionId];
      const willBeExpanded = isCurrentlyCollapsed; // Toggle: collapsed → expanded

      setCollapsed((prev) => {
        const next: CollapsedState = { ...prev, [sectionId]: !prev[sectionId] };
        writeCollapsed(next);
        return next;
      });

      // Lazy-load: nur wenn expanded wird UND noch nicht initialisiert
      if (willBeExpanded) {
        setSectionsState((prev) => {
          if (!prev[sectionId]?.initialized) {
            // Side-Effect ausserhalb des Setters — setTimeout 0 garantiert nach Render
            setTimeout(() => fetchSection(sectionId, 1, filters, kategorie), 0);
          }
          return prev;
        });
      }
    },
    [collapsed, fetchSection, filters, kategorie],
  );

  const handlePageChange = useCallback(
    (sectionId: SectionId, page: number) => {
      setSectionsState((prev) => ({
        ...prev,
        [sectionId]: { ...(prev[sectionId] ?? emptySectionState()), page },
      }));
      fetchSection(sectionId, page, filters, kategorie);
    },
    [fetchSection, filters, kategorie],
  );

  const handleClearSubFilter = useCallback(() => {
    setSubFilter({ dhlStatus: null });
  }, []);

  // Donut-Click: Sektion expandieren + zur Sektion scrollen
  const handleSegmentClick = useCallback(
    (sectionId: SectionId) => {
      // Expandieren (wenn collapsed)
      setCollapsed((prev) => {
        if (!prev[sectionId]) return prev; // schon expanded
        const next: CollapsedState = { ...prev, [sectionId]: false };
        writeCollapsed(next);
        return next;
      });

      // Lazy-load wenn noetig
      setSectionsState((prev) => {
        if (!prev[sectionId]?.initialized) {
          setTimeout(() => fetchSection(sectionId, 1, filters, kategorie), 0);
        }
        return prev;
      });

      // Scroll mit kleinem Delay damit DOM die Expand-Animation gerendert hat
      setTimeout(() => sectionListRef.current?.scrollToSection(sectionId), 100);
    },
    [fetchSection, filters, kategorie],
  );

  // Bar-Click: subFilter setzen + Carrier-Sektion expandieren + scrollen
  const handleBarClick = useCallback(
    (dhlStatus: InternalStatusKey) => {
      setSubFilter({ dhlStatus });

      setCollapsed((prev) => {
        if (!prev.carrier) return prev; // schon expanded
        const next: CollapsedState = { ...prev, carrier: false };
        writeCollapsed(next);
        return next;
      });

      setSectionsState((prev) => {
        if (!prev.carrier?.initialized) {
          setTimeout(() => fetchSection('carrier', 1, filters, kategorie), 0);
        }
        return prev;
      });

      setTimeout(() => sectionListRef.current?.scrollToSection('carrier'), 100);
    },
    [fetchSection, filters, kategorie],
  );

  // Gesamt-Count fuer den Header-Untertitel
  const totalCount = stats
    ? stats.trackingStatus.stuck +
      stats.trackingStatus.open +
      stats.trackingStatus.awaiting_package +
      stats.trackingStatus.carrier +
      stats.trackingStatus.manual_only +
      stats.trackingStatus.done +
      stats.trackingStatus.closed_manually
    : 0;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#E9FF57] rounded">
            <PackageSearch className="w-6 h-6 text-[#1A1A1A]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A]">Auftragsverfolgung</h1>
            <p className="text-xs text-gray-500">
              {totalCount.toLocaleString('de-DE')} Auftrag{totalCount === 1 ? '' : 'e'} insgesamt
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={triggerReload}
          disabled={statsLoading}
          className="p-2 rounded border border-[#DAD9D9] hover:bg-gray-50 disabled:opacity-50"
          title="Aktualisieren"
        >
          <RefreshCw
            className={`w-4 h-4 text-[#1A1A1A] ${statsLoading ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      {/* Quick-Task 260831-nnv: Sync-Status je Flow — letzter Lauf + was gerade laeuft.
          Bewusst ausserhalb der Tabs, damit der Zustand der Hintergrund-Syncs auf
          jedem Tab sichtbar ist (30s-Refresh, unabhaengig vom Dashboard-Reload). */}
      <SyncStatusBar />

      {/* Phase 11+12+14: Tabs Paket DHL / Lieferanten-Versand / Nachverfolgung / Einstellungen */}
      <div className="flex items-center gap-1 border-b border-[#DAD9D9]">
        {(['paket', 'lieferanten', 'followup'] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              setActiveView('dashboard');
              handleKategorieChange(k);
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'dashboard' && kategorie === k
                ? 'border-[#1A1A1A] text-[#1A1A1A]'
                : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
            }`}
          >
            {k === 'paket'
              ? 'Paket DHL'
              : k === 'lieferanten'
                ? 'Lieferanten-Versand'
                : 'Nachverfolgung'}
          </button>
        ))}
        {/* Phase 15: Statistik-Tab — 4. Tab vor Einstellungen */}
        <button
          type="button"
          onClick={() => setActiveView('statistik')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeView === 'statistik'
              ? 'border-[#1A1A1A] text-[#1A1A1A]'
              : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
          }`}
        >
          Statistik
        </button>
        {/* Phase 12: Einstellungen-Tab rechtsbuendig (ml-auto) */}
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setActiveView('einstellungen')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeView === 'einstellungen'
                ? 'border-[#1A1A1A] text-[#1A1A1A]'
                : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
            }`}
          >
            <Settings className="w-4 h-4" />
            Einstellungen
          </button>
        </div>
      </div>

      {/* Phase 12+15: Einstellungen / Statistik / Dashboard */}
      {activeView === 'einstellungen' ? (
        <SettingsTab />
      ) : activeView === 'statistik' ? (
        <StatisticsTab />
      ) : (
        <>
          {/* Phase 11+14: Erklaerungsbanner pro Tab */}
          <div className="border-l-4 border-[#E9FF57] bg-yellow-50 p-3 text-sm text-[#1A1A1A]">
            {kategorie === 'paket' && (
              'Hier siehst du alle Auftraege, die ueber DHL versendet wurden — inkl. detailliertem DHL-Sendungsverlauf. Auch Huck-Auftraege die per DHL verschickt wurden landen hier nach dem Versand.'
            )}
            {kategorie === 'lieferanten' && (
              'Hier siehst du alle Auftraege, die aktuell bei einem unserer Lieferanten in Bearbeitung sind. Sobald ein Auftrag verschickt wird (Status ≥7), wandert er entweder in den Paket-DHL-Tab (wenn DHL-Versand) oder wird im Lieferanten-Tab als versendet markiert (GLS/Spedition).'
            )}
            {kategorie === 'followup' && (
              'Auftraege die du manuell auf "Zur Nachverfolgung" gesetzt hast. Toggle auf jeder Karte ueber den Stern-Button rechts oben. Status-unabhaengig — auch versendete und manuell geschlossene Auftraege bleiben hier sichtbar bis du sie wieder entmarkierst.'
            )}
          </div>

          {/* Stats-Fehler */}
          {statsError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-800 border border-red-200 rounded text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Stats-Fehler: {statsError}
            </div>
          )}

          {/* Dashboard-Chart */}
          <DashboardChart
            stats={stats}
            kategorie={kategorie}
            loading={statsLoading}
            onSegmentClick={handleSegmentClick}
            onBarClick={handleBarClick}
          />

          {/* Phase 11: FilterBar — Carrier-Filter auf Lieferanten-Tab ausblenden */}
          <FilterBar
            filters={filters}
            onChange={handleFilterChange}
            showCarrierFilter={kategorie === 'paket'}
          />

          {/* Phase 17: Tab-Layout — Reiter statt Sektionen untereinander */}
          {kategorie === 'paket' && stats && (
            <SectionTabs
              items={paketTabItems}
              activeId={activePaketTab}
              onChange={handleSelectPaketTab}
            />
          )}

          {/* 20.08.2026: "Geklärt"-Haken + 24.08.2026: "Abgeschlossene anzeigen" —
              nur im Rücksendungs-Reiter sichtbar. Der Badge zaehlt immer nur
              OFFENE + UNGEKLAERTE Faelle; die Haken laden zusaetzlich die
              geklaerten bzw. (auto-)abgeschlossenen in die Liste. */}
          {kategorie === 'paket' && activePaketTab === 'ruecksendung' && (
            <div className="flex items-center gap-5 -mt-1">
              <label className="flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={mitGeklaerten}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setMitGeklaerten(next);
                    mitGeklaertenRef.current = next;
                    fetchSection('ruecksendung', 1, filters, kategorie);
                  }}
                  className="w-4 h-4 accent-[#1A1A1A]"
                />
                Geklärte anzeigen
              </label>
              <label className="flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={mitAbgeschlossenen}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setMitAbgeschlossenen(next);
                    mitAbgeschlossenenRef.current = next;
                    fetchSection('ruecksendung', 1, filters, kategorie);
                  }}
                  className="w-4 h-4 accent-[#1A1A1A]"
                />
                Abgeschlossene anzeigen
              </label>
            </div>
          )}

          {/* 24.08.2026: "Abgeschlossene anzeigen" — nur im Nie-versendet-Reiter.
              Der Badge zaehlt immer nur OFFENE Faelle; der Haken laedt zusaetzlich
              die (auto-)geschlossenen Geistersendungen in die Liste. */}
          {kategorie === 'paket' && activePaketTab === 'neverShipped' && (
            <label className="flex items-center gap-2 text-sm text-[#1A1A1A] cursor-pointer select-none -mt-1">
              <input
                type="checkbox"
                checked={mitAbgeschlossenen}
                onChange={(e) => {
                  const next = e.target.checked;
                  setMitAbgeschlossenen(next);
                  mitAbgeschlossenenRef.current = next;
                  fetchSection('neverShipped', 1, filters, kategorie);
                }}
                className="w-4 h-4 accent-[#1A1A1A]"
              />
              Abgeschlossene anzeigen
            </label>
          )}
          {kategorie === 'lieferanten' && stats && (
            <div className="flex flex-col gap-2">
              <SectionTabs
                items={lieferantenSupplierTabItems}
                activeId={activeLieferantenSupplier}
                onChange={handleSelectLieferantenSupplier}
              />
              <SectionTabs
                items={lieferantenSubItems}
                activeId={activeLieferantenSub}
                onChange={handleSelectLieferantenSub}
                className="text-xs"
              />
            </div>
          )}

          {/* SectionList */}
          <SectionList
            ref={sectionListRef}
            sectionsState={sectionsState}
            collapsed={collapsed}
            kategorie={kategorie}
            stats={stats}
            subFilter={subFilter}
            onToggleCollapsed={handleToggleCollapsed}
            onPageChange={handlePageChange}
            onClearSubFilter={handleClearSubFilter}
            onSelectOrder={setSelectedOrderId}
            onOrderClosed={triggerReload}
            onGeklaertToggled={triggerReload}
            activeSectionId={activeSectionId}
          />

          {/* Detail-Drawer (on demand) */}
          {selectedOrderId !== null && (
            <OrderDetailDrawer
              orderId={selectedOrderId}
              onClose={() => setSelectedOrderId(null)}
              onChanged={triggerReload}
            />
          )}
        </>
      )}
    </div>
  );
}
